import React from "react";
import { ArrowUpRight, CheckCircle2, Calendar } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";
import { WORKSHOP_URL } from "@/lib/defaults";
import { trackCta } from "@/lib/analytics";

/** Append (or replace) the utm_source query param on the workshop URL. */
const withUtmSource = (rawUrl, utmSource) => {
    if (!rawUrl) return "";
    try {
        const u = new URL(rawUrl);
        u.searchParams.set("utm_source", utmSource);
        return u.toString();
    } catch {
        // Fallback for non-absolute URLs
        const sep = rawUrl.includes("?") ? "&" : "?";
        return `${rawUrl}${sep}utm_source=${encodeURIComponent(utmSource)}`;
    }
};

export const FeaturedProgramme = () => {
    const { content } = useContent();
    const f = content.featured_programme;

    if (!f || f.visible === false) return null;

    const baseUrl = f.workshop_url || WORKSHOP_URL;
    const ctaHref = withUtmSource(baseUrl, "aurea-home");

    return (
        <section
            id="featured-programme"
            data-testid="featured-programme-section"
            className="bg-white py-24 lg:py-28 border-b border-slate-100"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    {/* Left: copy + highlights + CTA */}
                    <div className="lg:col-span-7">
                        <p className="label-eyebrow">{f.eyebrow}</p>
                        <h2
                            data-testid="featured-programme-title"
                            className="mt-4 font-display font-semibold text-slate-900 text-3xl sm:text-4xl lg:text-[42px] leading-[1.1] tracking-tight"
                        >
                            {f.title}
                        </h2>
                        <p className="mt-5 text-base lg:text-lg text-slate-600 font-body leading-relaxed">
                            {f.copy}
                        </p>

                        {Array.isArray(f.highlights) && f.highlights.length > 0 && (
                            <ul
                                data-testid="featured-programme-highlights"
                                className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3"
                            >
                                {f.highlights.map((h, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-2.5 text-[14px] text-slate-700 font-body"
                                    >
                                        <CheckCircle2
                                            className="w-4 h-4 text-teal-600 mt-0.5 shrink-0"
                                            strokeWidth={2}
                                        />
                                        <span>{h}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="mt-10">
                            <a
                                href={ctaHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="featured-programme-cta"
                                onClick={() =>
                                    trackCta("workshop_click", {
                                        source: "featured_programme_section",
                                    })
                                }
                                className="btn-base btn-primary"
                            >
                                {f.cta_label}
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    {/* Right: Next Cohort card */}
                    <aside
                        data-testid="featured-programme-cohort-card"
                        className="lg:col-span-5"
                    >
                        <div className="border border-slate-200 bg-slate-50 p-7 lg:p-8">
                            <div className="flex items-center justify-between">
                                <span className="label-eyebrow !text-slate-500">
                                    {f.cohort_label}
                                </span>
                                <span className="inline-flex items-center justify-center w-9 h-9 bg-slate-900 text-teal-300">
                                    <Calendar strokeWidth={1.5} className="w-4 h-4" />
                                </span>
                            </div>
                            <h3 className="mt-3 font-display text-xl font-semibold text-slate-900">
                                {f.cohort_title}
                            </h3>
                            {Array.isArray(f.cohort_lines) && (
                                <ul className="mt-3 space-y-1 text-[13.5px] text-slate-600 font-body">
                                    {f.cohort_lines.map((line, i) => (
                                        <li key={i}>{line}</li>
                                    ))}
                                </ul>
                            )}

                            {Array.isArray(f.schedule) && f.schedule.length > 0 && (
                                <div className="mt-6 pt-5 border-t border-slate-200">
                                    <p className="text-[11.5px] uppercase tracking-[0.18em] text-slate-500 font-medium">
                                        {f.schedule_heading || "Workshop dates"}
                                    </p>
                                    <ol
                                        data-testid="featured-programme-schedule"
                                        className="mt-3 divide-y divide-slate-200"
                                    >
                                        {f.schedule.map((s, i) => (
                                            <li
                                                key={i}
                                                className="flex items-start gap-3 py-2.5"
                                            >
                                                <span className="font-display text-[12px] font-semibold text-teal-700 w-20 shrink-0 pt-0.5">
                                                    {s.date}
                                                </span>
                                                <span className="text-[13.5px] text-slate-700 font-body leading-snug">
                                                    {s.title}
                                                </span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
};

export default FeaturedProgramme;
